/**
 * Avatar service for managing user avatars
 */

import { directApi } from './api';

class AvatarService {
  /**
   * Get user's current avatar URL
   * @returns {Promise<Object>} Avatar data
   */
  async getUserAvatar() {
    try {
      const response = await directApi.get('/user/avatar');
      return response.data;
    } catch (error) {
      console.error('Error getting user avatar:', error);
      throw error;
    }
  }

  /**
   * Refresh user's avatar (regenerate from email)
   * @returns {Promise<Object>} Updated avatar data
   */
  async refreshUserAvatar() {
    try {
      const response = await directApi.post('/user/avatar/refresh');
      return response.data;
    } catch (error) {
      console.error('Error refreshing user avatar:', error);
      throw error;
    }
  }

  /**
   * Get Gravatar URL for any email
   * @param {string} email - Email address
   * @param {number} size - Avatar size in pixels
   * @param {string} defaultType - Default image type
   * @returns {Promise<Object>} Gravatar data
   */
  async getGravatarUrl(email, size = 200, defaultType = 'identicon') {
    try {
      const response = await directApi.get(`/avatar/gravatar/${encodeURIComponent(email)}`, {
        params: { size, default: defaultType }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting Gravatar URL:', error);
      throw error;
    }
  }

  /**
   * Generate MD5 hash for Gravatar (client-side utility)
   * @param {string} email - Email address
   * @returns {string} MD5 hash
   */
  generateGravatarHash(email) {
    // Simple MD5 implementation for client-side Gravatar URLs
    // Note: For production, consider using a proper crypto library
    // Use a simple hash function for browser compatibility
    const cleanEmail = email.toLowerCase().trim();
    let hash = 0;
    for (let i = 0; i < cleanEmail.length; i++) {
      const char = cleanEmail.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Convert to hex string
    return Math.abs(hash).toString(16).padStart(8, '0');
  }

  /**
   * Generate Gravatar URL client-side
   * @param {string} email - Email address
   * @param {number} size - Avatar size
   * @param {string} defaultType - Default image type
   * @returns {string} Gravatar URL
   */
  getGravatarUrlSync(email, size = 200, defaultType = 'identicon') {
    // For now, use a simple fallback - in production, use backend hash
    const hash = this.generateGravatarHash(email);
    return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=${defaultType}`;
  }

  /**
   * Generate initials-based avatar URL using local canvas generation
   * @param {string} name - User's name
   * @param {string} email - User's email (for color consistency)
   * @param {number} size - Avatar size
   * @returns {string} Data URL for generated avatar
   */
  getInitialsAvatarUrl(name, email, size = 200) {
    // Extract initials
    let initials = 'U';
    if (name && name.trim()) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        initials = `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      } else {
        initials = parts[0].substring(0, 2).toUpperCase();
      }
    } else if (email) {
      initials = email.substring(0, 2).toUpperCase();
    }

    // Generate consistent color from email/name hash
    const hash = this.generateGravatarHash(email || name || 'default');
    const hue = parseInt(hash.substring(0, 2), 16) * 360 / 255;
    const backgroundColor = `hsl(${hue}, 60%, 50%)`;

    // Create canvas and generate avatar
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Draw background circle
    ctx.fillStyle = backgroundColor;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
    ctx.fill();

    // Draw initials text
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2);

    // Return data URL
    return canvas.toDataURL('image/png');
  }

  /**
   * Get best avatar URL with fallback chain
   * @param {string} email - User's email
   * @param {string} name - User's name
   * @param {number} size - Avatar size
   * @returns {Promise<string>} Best available avatar URL
   */
  async getBestAvatarUrl(email, name, size = 200) {
    try {
      // Try to get from backend (includes Gravatar check)
      const avatarData = await this.getUserAvatar();
      if (avatarData.avatar_url) {
        return avatarData.avatar_url;
      }
    } catch (error) {
      console.warn('Could not get avatar from backend, using fallback');
    }

    // Fallback to initials avatar
    return this.getInitialsAvatarUrl(name, email, size);
  }
}

// Export singleton instance
export const avatarService = new AvatarService();
export default avatarService;
